import { formatDistanceToNow } from "date-fns";
import { Calendar, CalendarCheck, CalendarClock } from "lucide-react";
import { Badge } from "../../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardMetadataItem,
  BaseEntityCardProps,
  BaseEntityCardTags,
  BaseEntityCardTitle,
} from "../BaseCard";

export interface Iteration {
  id: string;
  title: string;
  duration?: number;
  field_id: string;
  completed?: boolean;
  start_date?: string;
}

type IterationCardProps = Iteration & BaseEntityCardProps;

export function IterationCardAvatar({
  completed,
  className,
  style,
}: Partial<IterationCardProps>) {
  const Icon = completed ? CalendarCheck : CalendarClock;
  const color = completed ? "text-lc-active" : "text-teal";

  return (
    <BaseEntityCardIcon className={className} style={style}>
      <Icon className={`h-5 w-5 ${color}`} />
    </BaseEntityCardIcon>
  );
}

export function IterationCard({
  platform,
  title,
  duration,
  completed,
  start_date,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: IterationCardProps) {
  const startDate = start_date ? new Date(start_date) : null;

  const formattedStartDate = startDate
    ? formatDistanceToNow(startDate, { addSuffix: true })
    : "";

  const getEndDate = () => {
    if (!startDate || !duration) return null;
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + duration);
    return endDate;
  };

  const endDate = getEndDate();
  const formattedEndDate = endDate
    ? formatDistanceToNow(endDate, { addSuffix: true })
    : "";

  const getDurationText = () => {
    if (!duration) return "";
    if (duration === 7) return "1 week";
    if (duration === 14) return "2 weeks";
    if (duration === 21) return "3 weeks";
    if (duration === 28) return "4 weeks";
    return `${duration} days`;
  };

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <IterationCardAvatar completed={completed} />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>{title}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          <BaseEntityCardTags>
            {completed !== undefined && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className={`text-xs ${
                        completed
                          ? "bg-spectrum-green-light/30 text-green-800"
                          : "bg-spectrum-blue-light/30 text-teal"
                      }`}
                    >
                      {completed ? "Completed" : "Active"}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Iteration Status</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {duration && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="text-xs">
                      {getDurationText()}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Duration</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </BaseEntityCardTags>

          {(formattedStartDate || formattedEndDate) && (
            <BaseEntityCardMetadata>
              {formattedStartDate && (
                <BaseEntityCardMetadataItem
                  icon={<Calendar className="h-3 w-3" />}
                >
                  {completed ? "Started" : "Starts"} {formattedStartDate}
                </BaseEntityCardMetadataItem>
              )}

              {formattedEndDate && (
                <span>
                  {completed ? "Ended" : "Ends"} {formattedEndDate}
                </span>
              )}
            </BaseEntityCardMetadata>
          )}
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
